import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  Paper,
} from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon, ContentCopy as ContentCopyIcon } from "@mui/icons-material";
import httpRequest from "../utils/httpRequest";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

const MAX_ENTRIES = 10;

const BulkSupplierLedgerForm = ({ 
  open, 
  onClose, 
  suppliers, 
  financialYears, 
  firms, 
  onSuccess 
}) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Initialize with one empty entry
  useEffect(() => {
    if (open) {
      setEntries([getEmptyEntry()]);
      setError(null);
      setSuccess(null);
    }
  }, [open]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Enter' && event.ctrlKey) {
        event.preventDefault();
        handleSubmit();
      }
    };

    if (open) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, entries]);

  const getEmptyEntry = () => ({
    id: 1,
    supplier_id: "",
    sub_supplier_name: "",
    bill_date: new Date().toISOString().split("T")[0],
    bill_number: "",
    bill_type: "gst",
    credit_amount: "",
    description: "Goods Bill",
    firm_id: "",
  });

  const handleAddEntry = () => {
    if (entries.length >= MAX_ENTRIES) {
      setError(`Maximum ${MAX_ENTRIES} entries allowed at once.`);
      return;
    }
    
    const newId = Math.max(...entries.map(e => e.id)) + 1;
    const newEntry = {
      ...getEmptyEntry(),
      id: newId,
    };
    setEntries([...entries, newEntry]);
  };

  const handleRemoveEntry = (id) => {
    if (entries.length > 1) {
      const newEntries = entries.filter((entry) => entry.id !== id);
      setEntries(newEntries);
    }
  };

  const handleCopyEntry = (entryToCopy) => {
    if (entries.length >= MAX_ENTRIES) {
      setError(`Maximum ${MAX_ENTRIES} entries allowed at once.`);
      return;
    }
    
    const newId = Math.max(...entries.map(e => e.id)) + 1;
    const newEntry = {
      ...entryToCopy,
      id: newId,
      credit_amount: "",
      bill_number: "",
      sub_supplier_name: "",
      description: "Goods Bill",
      bill_type: "gst",
    };
    setEntries(prev => [...prev, newEntry]);
  };

  const handleEntryChange = (id, field, value) => {
    setEntries(prev => 
      prev.map(entry => 
        entry.id === id ? { ...entry, [field]: value } : entry
      )
    );
  };

  const validateEntries = () => {
    for (let i = 0; i < entries.length; i++) {
      const entry = entries[i];
      if (!entry.supplier_id) {
        setError(`Entry ${i + 1}: Supplier is required`);
        return false;
      }
      if (!entry.bill_date) {
        setError(`Entry ${i + 1}: Bill Date is required`);
        return false;
      }
      if (!getFinancialYearIdByDate(entry.bill_date)) {
        setError(`Entry ${i + 1}: No financial year found for the selected date`);
        return false;
      }
      if (!entry.bill_number) {
        setError(`Entry ${i + 1}: Bill Number is required`);
        return false;
      }
      if (!entry.credit_amount || entry.credit_amount <= 0) {
        setError(`Entry ${i + 1}: Credit Amount must be greater than 0`);
        return false;
      }
      if (!entry.firm_id) {
        setError(`Entry ${i + 1}: Firm is required`);
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateEntries()) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const entriesToSubmit = entries.map(entry => ({
        supplier_id: entry.supplier_id,
        financial_year_id: getFinancialYearIdByDate(entry.bill_date),
        sub_supplier_name: entry.sub_supplier_name,
        bill_date: entry.bill_date,
        bill_number: entry.bill_number,
        bill_type: entry.bill_type,
        credit_amount: entry.credit_amount,
        description: entry.description,
        firm_id: entry.firm_id,
      }));

      const response = await httpRequest(
        "suppliers/addBulkEntriesToLedger",
        "POST",
        { entries: entriesToSubmit }
      );

      setSuccess(response.message);
      
      if (onSuccess) {
        onSuccess();
      }

      setEntries([getEmptyEntry()]);
      setError(null);
      
      setTimeout(() => {
        handleClose();
      }, 2000);

    } catch (err) {
      setError(err.response?.data?.error || "Failed to add supplier ledgers");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEntries([]);
    setError(null);
    setSuccess(null);
    onClose();
  };

  const getFinancialYearIdByDate = (dateString) => {
    if (!dateString || !financialYears.length) return "";
    
    const date = new Date(dateString);
    for (const year of financialYears) {
      const startDate = new Date(year.start_date);
      const endDate = new Date(year.end_date);
      if (date >= startDate && date <= endDate) {
        return year.id;
      }
    }
    return financialYears.length > 0 ? financialYears[0].id : "";
  };

  const getCurrentFinancialYearId = () => {
    const currentDate = new Date();
    for (const year of financialYears) {
      const startDate = new Date(year.start_date);
      const endDate = new Date(year.end_date);
      if (currentDate >= startDate && currentDate <= endDate) {
        return year.id;
      }
    }
    return financialYears.length > 0 ? financialYears[0].id : "";
  };

  const getIncompleteEntriesCount = () => {
    return entries.filter(entry => 
      !entry.supplier_id || 
      !entry.bill_date || 
      !getFinancialYearIdByDate(entry.bill_date) ||
      !entry.bill_number || 
      !entry.credit_amount || 
      !entry.firm_id
    ).length;
  };

  const getTotalAmount = () => {
    return entries
      .filter(entry => entry.credit_amount && !isNaN(parseFloat(entry.credit_amount)))
      .reduce((sum, entry) => sum + parseFloat(entry.credit_amount), 0);
  };

  const getUniqueSuppliersCount = () => {
    return new Set(entries.filter(entry => entry.supplier_id).map(entry => entry.supplier_id)).size;
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          width: "95vw",
          maxWidth: "1400px",
          maxHeight: "85vh",
          m: 1
        },
      }}
    >
      <DialogTitle sx={{ pb: 0.5, px: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" sx={{ mb: 0.25 }}>
              Bulk Supplier Ledger Entry Form
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Maximum {MAX_ENTRIES} entries per submission
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 1, px: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Box sx={{ mb: 1.5, display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5, fontWeight: 'medium' }}>
            Quick Actions:
          </Typography>
          <Chip
            label="Today"
            size="small"
            variant="outlined"
            disabled={loading}
            onClick={() => {
              const today = new Date().toISOString().split("T")[0];
              setEntries(prev => prev.map(entry => ({ ...entry, bill_date: today })));
            }}
            sx={{ cursor: 'pointer', fontSize: '0.7rem', height: '24px', '&:hover': { bgcolor: 'action.hover' } }}
          />
          <Chip
            label="GST"
            size="small"
            variant="outlined"
            color="primary"
            disabled={loading}
            onClick={() => {
              setEntries(prev => prev.map(entry => ({ ...entry, bill_type: "gst" })));
            }}
            sx={{ cursor: 'pointer', fontSize: '0.7rem', height: '24px', '&:hover': { bgcolor: 'primary.light' } }}
          />
          <Chip
            label="Kachcha"
            size="small"
            variant="outlined"
            color="secondary"
            disabled={loading}
            onClick={() => {
              setEntries(prev => prev.map(entry => ({ ...entry, bill_type: "kachcha" })));
            }}
            sx={{ cursor: 'pointer', fontSize: '0.7rem', height: '24px', '&:hover': { bgcolor: 'secondary.light' } }}
          />
          <Chip
            label="Clear"
            size="small"
            variant="outlined"
            color="warning"
            disabled={loading}
            onClick={() => {
              setEntries(prev => prev.map(entry => ({
                ...entry,
                supplier_id: "",
                sub_supplier_name: "",
                bill_number: "",
                credit_amount: "",
                description: "Goods Bill",
                bill_type: "gst",
              })));
            }}
            sx={{ cursor: 'pointer', fontSize: '0.7rem', height: '24px', '&:hover': { bgcolor: 'warning.light' } }}
          />
        </Box>

        <Box sx={{ mb: 1.5, p: 1, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid #e0e0e0' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Typography 
              variant="caption" 
              fontWeight="bold" 
              color={entries.length >= MAX_ENTRIES ? 'error.main' : entries.length >= MAX_ENTRIES - 2 ? 'warning.main' : 'text.secondary'}
            >
              Summary: {entries.length}/{MAX_ENTRIES} entries • {getUniqueSuppliersCount()} suppliers
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              <Chip
                label={`Total: ₹${getTotalAmount().toFixed(2)}`}
                color="success"
                size="small"
                variant="filled"
                sx={{ fontSize: '0.75rem' }}
              />
              <Chip
                label={`Incomplete: ${getIncompleteEntriesCount()}`}
                color={getIncompleteEntriesCount() > 0 ? "error" : "success"}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.75rem' }}
              />
            </Box>
          </Box>
        </Box>

        <Box sx={{ maxHeight: '65vh', overflowY: 'auto' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: 1,
              bgcolor: 'grey.100',
              borderBottom: '2px solid #e0e0e0',
              fontWeight: 'bold',
              fontSize: '0.75rem',
              color: 'text.secondary',
              position: 'sticky',
              top: 0,
              zIndex: 1,
            }}
          >
            <Box sx={{ minWidth: '40px', textAlign: 'center' }}>#</Box>
            <Box sx={{ flex: 2, minWidth: '200px' }}>Supplier</Box>
            <Box sx={{ flex: 1, minWidth: '120px' }}>Sub-Supplier</Box>
            <Box sx={{ flex: 1, minWidth: '120px' }}>Firm</Box>
            <Box sx={{ flex: 1, minWidth: '100px' }}>Date</Box>
            <Box sx={{ flex: 1, minWidth: '100px' }}>Bill No. *</Box>
            <Box sx={{ flex: 0.5, minWidth: '80px' }}>Type</Box>
            <Box sx={{ flex: 1, minWidth: '100px' }}>Amount</Box>
            <Box sx={{ flex: 1.5, minWidth: '150px' }}>Description</Box>
            <Box sx={{ minWidth: '80px', textAlign: 'center' }}>Actions</Box>
          </Box>

          {entries.map((entry, index) => (
            <Box
              key={entry.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1,
                borderBottom: '1px solid #e0e0e0',
                bgcolor: index % 2 === 0 ? 'background.paper' : 'grey.50',
                '&:hover': { bgcolor: 'action.hover' },
                minHeight: '48px',
                position: 'relative',
              }}
            >
              <Box sx={{ minWidth: '40px', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                  #{index + 1}
                </Typography>
                <Tooltip title={
                  (entry.supplier_id && entry.bill_date && getFinancialYearIdByDate(entry.bill_date) && entry.bill_number && entry.credit_amount && entry.firm_id) ?
                    `Row ${index + 1} - Complete` :
                    `Row ${index + 1} - Incomplete (missing: ${[
                      !entry.supplier_id && 'supplier',
                      !entry.bill_date && 'date',
                      !getFinancialYearIdByDate(entry.bill_date) && 'valid financial year for date',
                      !entry.bill_number && 'bill number',
                      !entry.credit_amount && 'amount',
                      !entry.firm_id && 'firm'
                    ].filter(Boolean).join(', ')})`
                }>
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      bgcolor: (entry.supplier_id && entry.bill_date && getFinancialYearIdByDate(entry.bill_date) && entry.bill_number && entry.credit_amount && entry.firm_id) ? 'success.main' : 'warning.main',
                      cursor: 'help',
                      flexShrink: 0
                    }}
                  />
                </Tooltip>
              </Box>

              <Box sx={{ flex: 2, minWidth: '200px' }}>
                <FormControl fullWidth size="small">
                  <Select
                    value={entry.supplier_id}
                    onChange={(e) => handleEntryChange(entry.id, "supplier_id", e.target.value)}
                    error={!entry.supplier_id}
                    displayEmpty
                    disabled={loading}
                    sx={{
                      bgcolor: 'background.paper',
                      '&:hover': { bgcolor: 'action.hover' }
                    }}
                  >
                    <MenuItem value="" disabled>
                      <em>Select Supplier</em>
                    </MenuItem>
                    {suppliers.map((supplier) => (
                      <MenuItem key={supplier.id} value={supplier.id}>
                        {supplier.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ flex: 1, minWidth: '120px' }}>
                <TextField
                  placeholder="Sub-supplier"
                  size="small"
                  fullWidth
                  value={entry.sub_supplier_name}
                  onChange={(e) => handleEntryChange(entry.id, "sub_supplier_name", e.target.value)}
                  disabled={loading}
                  sx={{
                    '& .MuiInputBase-root': {
                      bgcolor: 'background.paper',
                      '&:hover': { bgcolor: 'action.hover' }
                    }
                  }}
                />
              </Box>

              <Box sx={{ flex: 1, minWidth: '120px' }}>
                <FormControl fullWidth size="small">
                  <Select
                    value={entry.firm_id}
                    onChange={(e) => handleEntryChange(entry.id, "firm_id", e.target.value)}
                    error={!entry.firm_id}
                    displayEmpty
                    disabled={loading}
                    sx={{
                      bgcolor: 'background.paper',
                      '&:hover': { bgcolor: 'action.hover' }
                    }}
                  >
                    <MenuItem value="" disabled>
                      <em>Select Firm</em>
                    </MenuItem>
                    {firms.map((firm) => (
                      <MenuItem key={firm.id} value={firm.id}>
                        {firm.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ flex: 1, minWidth: '100px' }}>
                <DatePicker
                  value={entry.bill_date ? dayjs(entry.bill_date) : null}
                  onChange={(newValue) => {
                    const dateString = newValue?.format("YYYY-MM-DD") || "";
                    handleEntryChange(entry.id, "bill_date", dateString);
                    
                    // Automatically set financial year based on the selected date
                    if (dateString) {
                      const financialYearId = getFinancialYearIdByDate(dateString);
                      if (financialYearId) {
                        handleEntryChange(entry.id, "financial_year_id", financialYearId);
                      }
                    }
                  }}
                  disabled={loading}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                      error: !entry.bill_date || !getFinancialYearIdByDate(entry.bill_date),
                      placeholder: "Date",
                      helperText: entry.bill_date && !getFinancialYearIdByDate(entry.bill_date) ? "No FY found for this date" : "",
                      sx: {
                        '& .MuiInputBase-root': {
                          bgcolor: 'background.paper',
                          '&:hover': { bgcolor: 'action.hover' }
                        }
                      }
                    },
                  }}
                  format="DD-MM-YYYY"
                />
              </Box>

              <Box sx={{ flex: 1, minWidth: '100px' }}>
                <TextField
                  placeholder="Bill No. *"
                  size="small"
                  fullWidth
                  value={entry.bill_number}
                  onChange={(e) => handleEntryChange(entry.id, "bill_number", e.target.value)}
                  error={!entry.bill_number}
                  disabled={loading}
                  sx={{
                    '& .MuiInputBase-root': {
                      bgcolor: 'background.paper',
                      '&:hover': { bgcolor: 'action.hover' }
                    }
                  }}
                />
              </Box>

              <Box sx={{ flex: 0.5, minWidth: '80px' }}>
                <FormControl fullWidth size="small">
                  <Select
                    value={entry.bill_type}
                    onChange={(e) => handleEntryChange(entry.id, "bill_type", e.target.value)}
                    disabled={loading}
                    sx={{
                      bgcolor: 'background.paper',
                      '&:hover': { bgcolor: 'action.hover' }
                    }}
                  >
                    <MenuItem value="">-</MenuItem>
                    <MenuItem value="kachcha">Kachcha</MenuItem>
                    <MenuItem value="gst">GST</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ flex: 1, minWidth: '100px' }}>
                <TextField
                  placeholder="Amount"
                  type="number"
                  size="small"
                  fullWidth
                  value={entry.credit_amount}
                  onChange={(e) => handleEntryChange(entry.id, "credit_amount", e.target.value)}
                  error={!entry.credit_amount}
                  disabled={loading}
                  inputProps={{ min: 0, step: 0.01 }}
                  sx={{
                    '& .MuiInputBase-root': {
                      bgcolor: 'background.paper',
                      '&:hover': { bgcolor: 'action.hover' }
                    }
                  }}
                />
              </Box>

              <Box sx={{ flex: 1.5, minWidth: '150px' }}>
                <TextField
                  placeholder="Description"
                  size="small"
                  fullWidth
                  value={entry.description}
                  onChange={(e) => handleEntryChange(entry.id, "description", e.target.value)}
                  disabled={loading}
                  sx={{
                    '& .MuiInputBase-root': {
                      bgcolor: 'background.paper',
                      '&:hover': { bgcolor: 'action.hover' }
                    }
                  }}
                />
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: '80px' }}>
                <Tooltip title={entries.length >= MAX_ENTRIES ? "Maximum entries reached" : "Copy entry"}>
                  <IconButton
                    size="small"
                    onClick={() => handleCopyEntry(entry)}
                    disabled={entries.length >= MAX_ENTRIES || loading}
                    sx={{ 
                      color: entries.length >= MAX_ENTRIES ? 'text.disabled' : 'primary.main', 
                      p: 0.25 
                    }}
                  >
                    <ContentCopyIcon sx={{ fontSize: '0.875rem' }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Remove entry">
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveEntry(entry.id)}
                    disabled={entries.length <= 1 || loading}
                    sx={{ color: 'error.main', p: 0.25 }}
                  >
                    <DeleteIcon sx={{ fontSize: '0.875rem' }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          ))}

          <Box sx={{ p: 0.5, borderTop: '1px solid #e0e0e0', bgcolor: 'background.paper', display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="text"
              startIcon={<AddIcon sx={{ fontSize: '0.875rem' }} />}
              onClick={handleAddEntry}
              disabled={entries.length >= MAX_ENTRIES || loading}
              size="small"
              sx={{
                fontSize: '0.7rem',
                py: 0.25,
                px: 1,
                minWidth: 'auto',
                color: entries.length >= MAX_ENTRIES || loading ? 'text.disabled' : 'primary.main',
                '&:hover': entries.length >= MAX_ENTRIES || loading ? {} : { bgcolor: 'primary.light', color: 'white' }
              }}
            >
              Add {entries.length >= MAX_ENTRIES && '(Max reached)'}
            </Button>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" color="text.secondary">
            💡 Use copy button to duplicate entries quickly
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={handleClose} variant="outlined" size="small" disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={
              loading ||
              entries.length > MAX_ENTRIES ||
              getIncompleteEntriesCount() > 0
            }
            size="small"
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
            title={
              loading
                ? 'Submitting...'
                : entries.length > MAX_ENTRIES 
                ? `Too many entries (${entries.length}/${MAX_ENTRIES})`
                : getIncompleteEntriesCount() > 0
                ? `${getIncompleteEntriesCount()} entries have missing required fields`
                : 'Ready to submit'
            }
          >
            {loading ? 'Submitting...' : `Submit (${entries.length})`}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default BulkSupplierLedgerForm; 